import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminErrorBanner,
  AdminFormField,
  AdminInfoRow,
  AdminLoadingState,
  AdminPageLayout,
  AdminSelect,
  AdminStatusBadge,
  AdminTextarea,
} from "@/components/admin";
import {
  fetchSoundRawAudioBlob,
  fetchSoundRawAudioReviewDetail,
  fetchSoundRawAudioValidationHistory,
  saveSoundRawAudioValidation,
  type AdminSoundRawAudioReviewDetail,
  type AdminSoundRawAudioValidationHistory,
  type AdminSoundValidationResultCode,
  type AdminSoundUserDisplayActionCode,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const formatConfidence = (value: number | null) => value == null ? "-" : `${Math.round(value * 100)}%`;
const userDisplayActionByResult: Record<AdminSoundValidationResultCode, AdminSoundUserDisplayActionCode> = {
  CONFIRMED: "KEEP_ORIGINAL",
  CORRECTED: "SHOW_CORRECTED",
  FALSE_POSITIVE: "HIDE",
  UNDETERMINED: "KEEP_ORIGINAL",
};
const userDisplayActionLabel: Record<AdminSoundUserDisplayActionCode, string> = {
  KEEP_ORIGINAL: "기존 표시 유지",
  SHOW_CORRECTED: "수정 결과 표시",
  HIDE: "숨김",
};

const SoundRawAudioReviewDetailPage = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const numericId = Number(eventId);
  const [detail, setDetail] = useState<AdminSoundRawAudioReviewDetail | null>(null);
  const [history, setHistory] = useState<AdminSoundRawAudioValidationHistory[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
  const [validationResultCode, setValidationResultCode] = useState<AdminSoundValidationResultCode>("CONFIRMED");
  const [reason, setReason] = useState("");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const userDisplayActionCode = userDisplayActionByResult[validationResultCode];
  const corrected = validationResultCode === "CORRECTED";

  const load = useCallback(async () => {
    if (!Number.isFinite(numericId)) { setErrorMessage("잘못된 원음 검수 경로입니다."); return; }
    setLoading(true);
    setErrorMessage(null);
    try {
      const [nextDetail, nextHistory] = await Promise.all([
        fetchSoundRawAudioReviewDetail(numericId),
        fetchSoundRawAudioValidationHistory(numericId),
      ]);
      setDetail(nextDetail);
      setHistory(nextHistory);
      setSelectedCandidateId(nextDetail.currentValidation?.selectedCandidateId ?? nextDetail.candidates[0]?.soundAnalysisEventCandidateId ?? null);
      setValidationResultCode(nextDetail.currentValidation?.validationResultCode ?? "CONFIRMED");
      setReason(nextDetail.currentValidation?.validationReason ?? "");
    } catch (error) {
      setDetail(null);
      setHistory([]);
      setErrorMessage(error instanceof Error ? error.message : "원음 검수 상세를 불러오지 못했습니다.");
    } finally { setLoading(false); }
  }, [numericId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const loadAudio = async () => {
    setLoadingAudio(true);
    setErrorMessage(null);
    try {
      const blob = await fetchSoundRawAudioBlob(numericId);
      setAudioUrl((previous) => { if (previous) URL.revokeObjectURL(previous); return URL.createObjectURL(blob); });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "원음 스트림을 불러오지 못했습니다.");
    } finally { setLoadingAudio(false); }
  };

  const save = async () => {
    if (!detail || !reason.trim()) { setErrorMessage("검수 사유를 입력하세요."); return; }
    const candidate = detail.candidates.find((item) => item.soundAnalysisEventCandidateId === selectedCandidateId) ?? null;
    if (corrected && (!candidate?.soundGroupCode || !candidate.displayLabelCode)) {
      setErrorMessage("수정 판정에는 그룹과 표시 라벨이 있는 후보를 선택해야 합니다."); return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await saveSoundRawAudioValidation(numericId, {
        validationResultCode,
        userDisplayActionCode,
        selectedCandidateId: corrected ? selectedCandidateId : null,
        correctedSoundGroupCode: corrected ? candidate?.soundGroupCode : null,
        correctedDisplayLabelCode: corrected ? candidate?.displayLabelCode : null,
        validationReason: reason.trim(),
      });
      await load();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "검수 결과를 저장하지 못했습니다.");
    } finally { setSaving(false); }
  };

  const metricRows = Object.entries(detail?.metric ?? {}).filter(([, value]) => value != null);
  return (
    <AdminPageLayout
      title="원음 검수 상세"
      subtitle={detail ? `이벤트 #${detail.soundAnalysisEventId} · ${formatDateTime(detail.analyzedAt, 0, 16)}` : "원음과 분석 결과를 함께 검수합니다."}
      actions={<AdminButton variant="secondary" size="sm" icon="ri-arrow-left-line" onClick={() => navigate("/admin/sound-raw-audio-reviews")}>목록</AdminButton>}
    >
      <div className="mt-4 space-y-4">
        <AdminErrorBanner message={errorMessage} />
        {loading && <AdminLoadingState message="원음 검수 상세를 불러오는 중입니다." />}
        {detail && <>
          <div className="grid gap-4 xl:grid-cols-3">
            <AdminCard className="p-4 xl:col-span-2">
              <div className="mb-3 flex items-center justify-between">
                <div><h3 className="text-sm font-bold text-slate-800">제한 원음 재생</h3><p className="mt-1 text-xs text-slate-500">사용자·기기·실제 파일 경로는 표시하지 않습니다.</p></div>
                <AdminStatusBadge group="RAW_AUDIO" code={detail.rawAudio.lifecycleStatusCode} label={detail.rawAudio.lifecycleStatusCode} />
              </div>
              {audioUrl ? <audio className="w-full" controls preload="none" src={audioUrl} /> : <AdminButton variant="primary" icon={loadingAudio ? "ri-loader-4-line animate-spin" : "ri-play-circle-line"} onClick={loadAudio} disabled={loadingAudio}>원음 불러오기</AdminButton>}
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <AdminInfoRow dense bordered label="길이" value={`${(detail.rawAudio.durationMs / 1000).toFixed(1)}초`} />
                <AdminInfoRow dense bordered label="샘플레이트" value={`${detail.rawAudio.sampleRateHz} Hz`} />
                <AdminInfoRow dense bordered label="채널" value={detail.rawAudio.channelCount} />
                <AdminInfoRow dense bordered label="보존 기한" value={formatDateTime(detail.rawAudio.retentionUntil, 0, 16)} />
              </div>
            </AdminCard>
            <AdminCard className="p-4">
              <h3 className="mb-3 text-sm font-bold text-slate-800">분석 요약</h3>
              <div className="space-y-1">
                <AdminInfoRow dense bordered label="대표 라벨" value={detail.representativeDisplayLabel ?? detail.representativeLabelCode ?? "-"} />
                <AdminInfoRow dense bordered label="그룹" value={detail.representativeGroupCode ?? "-"} />
                <AdminInfoRow dense bordered label="신뢰도" value={formatConfidence(detail.representativeConfidence)} />
                <AdminInfoRow dense bordered label="모델" value={`${detail.modelName ?? "-"} ${detail.modelVersion ?? ""}`} />
                <AdminInfoRow dense bordered label="정책" value={detail.activationPolicyVersion ?? "-"} />
              </div>
            </AdminCard>
          </div>

          <AdminCard className="p-4">
            <h3 className="mb-3 text-sm font-bold text-slate-800">모델 원본 후보</h3>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {detail.candidates.map((candidate) => {
                const selected = selectedCandidateId === candidate.soundAnalysisEventCandidateId;
                return <AdminButton key={candidate.soundAnalysisEventCandidateId} variant="plain" size="auto" onClick={() => setSelectedCandidateId(candidate.soundAnalysisEventCandidateId)} className={`rounded-xl border p-3 text-left ${selected ? "border-violet-300 bg-violet-50" : "border-slate-100 bg-white hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between"><span className="text-xs font-black text-slate-700">Rank {candidate.rank}</span><span className="text-xs font-bold text-blue-700">{formatConfidence(candidate.score)}</span></div>
                  <p className="mt-2 text-sm font-semibold text-slate-800">{candidate.displayLabel ?? candidate.displayLabelCode ?? candidate.modelLabelCode}</p>
                  <p className="mt-1 text-xs text-slate-400">{candidate.soundGroupCode ?? "미매핑"} · {candidate.riskLevelCode ?? "-"}</p>
                </AdminButton>;
              })}
            </div>
          </AdminCard>

          <div className="grid gap-4 xl:grid-cols-2">
            <AdminCard className="p-4">
              <h3 className="mb-3 text-sm font-bold text-slate-800">검수 판정</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <AdminFormField label="판정 결과"><AdminSelect value={validationResultCode} onValueChange={(nextValue) => setValidationResultCode(nextValue as AdminSoundValidationResultCode)}><option value="CONFIRMED">확인</option><option value="CORRECTED">라벨 수정</option><option value="FALSE_POSITIVE">오탐</option><option value="UNDETERMINED">보류</option></AdminSelect></AdminFormField>
                <AdminInfoRow dense bordered label="사용자 표시" value={userDisplayActionLabel[userDisplayActionCode]} />
              </div>
              <AdminFormField label="검수 사유" className="mt-3"><AdminTextarea rows={4} className="text-sm" maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="판정 근거를 입력하세요." /></AdminFormField>
              <div className="mt-3 flex justify-end"><AdminButton variant="primary" icon={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} onClick={save} disabled={saving}>검수 저장</AdminButton></div>
            </AdminCard>
            <AdminCard className="p-4">
              <h3 className="mb-3 text-sm font-bold text-slate-800">검수 이력</h3>
              <div className="space-y-2">{history.map((item) => <div key={item.version} className="rounded-xl border border-slate-100 bg-slate-50 p-3"><div className="flex items-center justify-between"><AdminStatusBadge group="SOUND_VALIDATION" code={item.validationResultCode} label={item.validationResultCode} /><span className="text-xs text-slate-400">v{item.version} · {formatDateTime(item.validatedAt, 0, 16)}</span></div><p className="mt-2 text-xs text-slate-600">{item.validationReason}</p></div>)}{history.length === 0 && <p className="text-sm text-slate-500">저장된 검수 이력이 없습니다.</p>}</div>
            </AdminCard>
          </div>

          {metricRows.length > 0 && <AdminCard className="p-4"><h3 className="mb-3 text-sm font-bold text-slate-800">음향 메트릭</h3><div className="grid gap-2 md:grid-cols-3">{metricRows.map(([key, value]) => <AdminInfoRow key={key} dense bordered label={key} value={String(value)} />)}</div></AdminCard>}
        </>}
      </div>
    </AdminPageLayout>
  );
};

export default SoundRawAudioReviewDetailPage;
